import React, { useMemo } from 'react'
import { useTranslation } from 'react-i18next'
import { Tooltip, Typography } from '@mui/material'
import Status from 'components/global/Status'
import { Box } from '@mui/system'
import StateIcon from 'components/global/StateIcon'
import CircleIcon from '@mui/icons-material/Circle'
import { availableToBgColor, availableToColor, availableToTranslationKey } from 'pages/Supervision/helpers'

const EquipmentsCount = ({ site, icon, iconweight, name, keyUp, keyTotal }) => {
  const { t } = useTranslation()
  const color = useMemo(() => {
    return site.equipment[keyUp] === site.equipment[keyTotal]
      ? '#92BC62'
      : site.equipment[keyUp] < site.equipment[keyTotal] && site.equipment[keyUp] > 0
        ? '#F59E0B'
        : '#DC2626'
  }, [site, keyUp, keyTotal])
  const title = useMemo(() => {
    return site.equipment[keyUp] === site.equipment[keyTotal] && name === 'ap'
      ? `${t('supervision.tables.connected_devices_col')} : ${site.equipment.nb_active_users}`
      : site.equipment[keyUp] === site.equipment[keyTotal] && name != 'ap'
        ? null
        : t(`supervision.tables.nb_${name}_tooltip`, {
            count: `${site.equipment[keyUp]} / ${site.equipment[keyTotal]}`
          })
  }, [name, site, keyUp, keyTotal])

  return (
    <Tooltip title={title} arrow placement="top" id={`tooltip-${name}-${site.id}`}>
      <Box
        data-tip="tooltip"
        data-for={`tooltip-${name}-${site.id}`}
        sx={{
          position: 'relative',
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          borderRadius: '4px',
          padding: '4px 6px',
          background: '#FAFAFA',
          width: '48px'
        }}
      >
        <StateIcon iconlabel={icon} iconweight={iconweight} iconcolor={'#A2AAB8'} iconsize="11px" />
        <Typography
          sx={{
            fontSize: '12px',
            color: '#727272'
          }}
        >
          {site.equipment[keyTotal]}
        </Typography>
        <CircleIcon
          sx={{
            position: 'absolute',
            left: '90%',
            bottom: '80%',
            color: color,
            fontSize: '10px'
          }}
        />
      </Box>
    </Tooltip>
  )
}

const COLUMNS_CONFIG = [
  {
    name: 'status',
    header: {
      label: ctx => ctx.t('coupons.table.status_col')
    },
    value: (site, ctx) => (
      <Status
        status={ctx.t(availableToTranslationKey(site.equipment ? site.equipment.available_level : 'down'))}
        color={availableToColor(site.equipment ? site.equipment.available_level : 'down')}
        bgcolor={availableToBgColor(site.equipment ? site.equipment.available_level : 'down')}
        isUppercase={false}
      />
    ),
    sortBy: 'status',
    sortable: true
  },
  {
    name: 'name',
    header: {
      label: ctx => ctx.t('supervision.tables.site_col')
    },
    value: site => site.name,
    sortBy: 'name',
    sortable: true
  },
  {
    name: 'nb_lines',
    header: {
      label: ctx => ctx.t('supervision.tables.nb_lines')
    },
    value: site => `${site.equipment.nb_lines_ok} / ${site.equipment.nb_lines}`
  },
  {
    name: 'firewalls',
    header: {
      label: ctx => ctx.t('supervision.tables.nb_firewall')
    },
    value: site =>
      site.equipment.nb_firewalls > 0 && (
        <EquipmentsCount
          site={site}
          keyUp={'nb_firewalls_ok'}
          keyTotal={'nb_firewalls'}
          iconweight={'light'}
          icon={'shield'}
          name={'firewall'}
        />
      )
  },
  {
    name: 'accesspoints',
    header: {
      label: ctx => ctx.t('supervision.tables.nb_ap')
    },
    value: site =>
      site.equipment.nb_accesspoints > 0 && (
        <EquipmentsCount
          site={site}
          keyUp={'nb_accesspoints_ok'}
          keyTotal={'nb_accesspoints'}
          icon={'signal-stream'}
          name={'ap'}
        />
      )
  },
  {
    name: 'switches',
    header: {
      label: ctx => ctx.t('supervision.tables.nb_switch')
    },
    value: site =>
      site.equipment.nb_switches > 0 && (
        <EquipmentsCount
          site={site}
          keyUp={'nb_switches_ok'}
          keyTotal={'nb_switches'}
          icon={'ethernet'}
          name={'switch'}
        />
      )
  },
  {
    name: 'esls',
    header: {
      label: ctx => ctx.t('supervision.tables.nb_esl')
    },
    value: site =>
      site.equipment.nb_esls > 0 && (
        <EquipmentsCount
          site={site}
          keyUp={'nb_esls_ok'}
          keyTotal={'nb_esls'}
          icon={'rectangle-barcode'}
          name={'esl'}
        />
      )
  },
  {
    name: 'chromecasts',
    header: {
      label: ctx => ctx.t('supervision.tables.nb_chromecast')
    },
    value: site =>
      site.equipment.nb_chromecasts > 0 && (
        <EquipmentsCount
          site={site}
          keyUp={'nb_chromecasts_ok'}
          keyTotal={'nb_chromecasts'}
          icon={'screencast'}
          name={'chromecast'}
        />
      )
  },
  {
    name: 'tvs',
    header: {
      label: ctx => ctx.t('supervision.tables.nb_tv')
    },
    value: site =>
      site.equipment.nb_tvs > 0 && (
        <EquipmentsCount site={site} keyUp={'nb_tvs_ok'} keyTotal={'nb_tvs'} icon={'tv'} name={'tv'} />
      )
  },
  {
    name: 'inverters',
    header: {
      label: ctx => ctx.t('supervision.tables.nb_inverter')
    },
    value: site =>
      site.equipment.nb_inverters > 0 && (
        <EquipmentsCount
          site={site}
          keyUp={'nb_inverters_ok'}
          keyTotal={'nb_inverters'}
          icon={'bolt'}
          name={'inverter'}
        />
      )
  },
  {
    name: 'cctv_cameras',
    header: {
      label: ctx => ctx.t('supervision.tables.nb_cctv_camera')
    },
    value: site =>
      site.equipment.nb_cctv_cameras > 0 && (
        <EquipmentsCount
          site={site}
          keyUp={'nb_cctv_cameras_ok'}
          keyTotal={'nb_cctv_cameras'}
          icon={'camera'}
          name={'cctv_camera'}
        />
      )
  },
  {
    name: 'cctv_servers',
    header: {
      label: ctx => ctx.t('supervision.tables.nb_cctv_server')
    },
    value: site =>
      site.equipment.nb_cctv_servers > 0 && (
        <EquipmentsCount
          site={site}
          keyUp={'nb_cctv_servers_ok'}
          keyTotal={'nb_cctv_servers'}
          icon={'server'}
          name={'cctv_server'}
        />
      )
  }
]

export default COLUMNS_CONFIG
